import React, { useEffect, useMemo, useState } from "react";
import { Badge, Form, InputGroup, Button } from "react-bootstrap";
import { toast } from "react-toastify";

const Procurement = () => {
    const [purchaseOrders, setPurchaseOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState("all");
    const [search, setSearch] = useState("");

    // Column styles to force single-line cells with ellipsis
    const columnStyles = {
        po: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160 },
        supplier: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 280 },
        created: { whiteSpace: 'nowrap' },
        expected: { whiteSpace: 'nowrap' },
        status: { whiteSpace: 'nowrap' },
        value: { whiteSpace: 'nowrap' },
        actions: { whiteSpace: 'nowrap' }
    };

    useEffect(() => {
        // TODO: Replace with API integration
        setLoading(true);
        const t = setTimeout(() => {
            setPurchaseOrders([]);
            setLoading(false);
        }, 600);
        return () => clearTimeout(t);
    }, []);

    const filteredPOs = useMemo(() => {
        return purchaseOrders.filter(po => {
            const matchesStatus = statusFilter === "all" || po.status === statusFilter;
            const q = search.trim().toLowerCase();
            const matchesSearch = q === "" || po.poNumber.toLowerCase().includes(q) || po.supplier.toLowerCase().includes(q);
            return matchesStatus && matchesSearch;
        });
    }, [purchaseOrders, statusFilter, search]);

    const totalValue = (po) => po.items.reduce((sum, it) => sum + (it.qty * it.unitPrice), 0);

    const onApprove = (id) => {
        setPurchaseOrders(prev => prev.map(po => po.id === id ? { ...po, status: "approved" } : po));
        toast.success("Purchase order approved");
    };

    const onMarkReceived = (id) => {
        setPurchaseOrders(prev => prev.map(po => po.id === id ? { ...po, status: "received" } : po));
        toast.success("Goods receipt recorded");
    };

    const onCreatePO = () => {
        toast.info("Create PO form coming soon");
    };

    const getStatusBadgeVariant = (status) => {
        switch (status?.toLowerCase()) {
            case "pending":
                return "warning";
            case "approved":
                return "info";
            case "received":
                return "success";
            default:
                return "secondary";
        }
    };

    const formatDate = (iso) => new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });

    return (
        <div className="container-fluid py-3">
            <div className="card mb-5">
                <div className="card-body">
                    <div className="align-items-center justify-content-between mb-3">
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <h5 className="mb-0 font-weight-bold mr-3" style={{ color: 'white' }}>Procurement / Purchase Orders</h5>
                            <Badge bg="info">Procurement</Badge>
                        </div>
                        <p className="text-muted mb-3">Manage supplier purchase orders and goods receipts</p>
                        <div className="row mt-2">
                            <div className="col-md-3">
                                <div className="card text-center">
                                    <div className="card-body">
                                        <h5 className="card-title">{purchaseOrders.length}</h5>
                                        <p className="card-text">Total POs</p>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card text-center">
                                    <div className="card-body">
                                        <h5 className="card-title text-warning">{purchaseOrders.filter(p => p.status === 'pending').length}</h5>
                                        <p className="card-text">Pending Approval</p>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card text-center">
                                    <div className="card-body">
                                        <h5 className="card-title text-info">{purchaseOrders.filter(p => p.status === 'approved').length}</h5>
                                        <p className="card-text">Approved</p>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card text-center">
                                    <div className="card-body">
                                        <h5 className="card-title text-success">{purchaseOrders.filter(p => p.status === 'received').length}</h5>
                                        <p className="card-text">Received</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex justify-content-between align-items-end mt-3" style={{ gap: 12 }}>
                            <div className="d-flex align-items-end" style={{ gap: 12 }}>
                                <Form.Group className="mb-0" style={{ minWidth: 150 }}>
                                    <Form.Label className="mb-1" style={{ fontSize: 13 }}>Status</Form.Label>
                                    <Form.Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} size="sm">
                                        <option value="all">All</option>
                                        <option value="pending">Pending</option>
                                        <option value="approved">Approved</option>
                                        <option value="received">Received</option>
                                    </Form.Select>
                                </Form.Group>
                                <Form.Group className="mb-0 flex-grow-1" style={{ maxWidth: 400 }}>
                                    <Form.Label className="mb-1" style={{ fontSize: 13, visibility: 'hidden' }}>Search</Form.Label>
                                    <Form.Control
                                        placeholder="Search by PO # or supplier"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        size="sm"
                                    />
                                </Form.Group>
                            </div>
                            <Button variant="primary" onClick={onCreatePO} size="sm" style={{ minWidth: 170, marginBottom: 0 }}>
                                <i className="fa fa-plus"></i> Create Purchase Order
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="card">
                <div className="card-body">
                    <div className="table-responsive">
                        <table hover size="sm" className="mb-0 align-middle">
                            <thead>
                                <tr>
                                    <th style={{ width: 160 }}>PO #</th>
                                    <th style={{ width: 280 }}>Supplier</th>
                                    <th style={{ width: 140 }}>Created</th>
                                    <th style={{ width: 140 }}>Expected</th>
                                    <th style={{ width: 120 }}>Status</th>
                                    <th style={{ width: 140 }}>Total Value</th>
                                    <th style={{ width: 220 }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="7" className="text-center py-4">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : filteredPOs.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="text-center py-5">
                                            <h5 className="text-muted mb-2">No purchase orders found</h5>
                                            <p className="text-muted mb-3">
                                                {search || statusFilter !== "all" ? "Try adjusting your search criteria" : "Get started by creating your first purchase order"}
                                            </p>
                                        </td>
                                    </tr>
                                ) : (
                                    filteredPOs.map(po => (
                                        <tr key={po.id}>
                                            <td data-label="PO #" style={columnStyles.po} title={po.poNumber}>
                                                <strong style={{ fontWeight: '600', color: '#1890ff' }}>{po.poNumber}</strong>
                                            </td>
                                            <td data-label="Supplier" style={columnStyles.supplier} title={po.supplier}>
                                                {po.supplier}
                                            </td>
                                            <td data-label="Created" style={columnStyles.created}>
                                                {formatDate(po.createdAt)}
                                            </td>
                                            <td data-label="Expected" style={columnStyles.expected}>
                                                {po.expectedDate ? po.expectedDate : "—"}
                                            </td>
                                            <td data-label="Status" style={columnStyles.status}>
                                                <Badge bg={getStatusBadgeVariant(po.status)}>
                                                    {po.status.charAt(0).toUpperCase() + po.status.slice(1)}
                                                </Badge>
                                            </td>
                                            <td data-label="Total Value" style={{ ...columnStyles.value, fontWeight: '600', color: '#52c41a' }}>
                                                ${totalValue(po).toFixed(2)}
                                            </td>
                                            <td data-label="Actions" style={columnStyles.actions}>
                                                <div className="d-flex gap-2" style={{ flexWrap: 'nowrap' }}>
                                                    {po.status === 'pending' && (
                                                        <Button variant="success" size="sm" onClick={() => onApprove(po.id)}>
                                                            Approve
                                                        </Button>
                                                    )}
                                                    {po.status === 'approved' && (
                                                        <Button variant="primary" size="sm" onClick={() => onMarkReceived(po.id)}>
                                                            Mark Received
                                                        </Button>
                                                    )}
                                                    <Button variant="outline-secondary" size="sm" onClick={() => alert(JSON.stringify(po, null, 2))}>
                                                        <i className="fa fa-eye"></i>
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Procurement;


